import { useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import { Switch } from "@/components/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

import { useRawEntityQuery, useSaveRawEntityMutation } from "../hooks/admin-queries"
import { adminQueryKeys } from "../hooks/admin-query-keys"
import { RawJsonTab } from "./shared/raw-json-tab"

type EditTrainingModuleDialogProps = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  moduleId: string | null
}

type StoredTrainingModule = {
  display_name?: string
  catalog_enabled?: boolean
  enabled?: boolean
  access?: Record<string, unknown>
  sections?: unknown[]
  [key: string]: unknown
}

/**
 * Edit dialog for a training module entity.
 *
 * - **Form** tab surfaces enabled + display name.
 * - **Raw JSON** tab shows the full stored document (including sections,
 *   access policy, and forward-compatible keys).
 *
 * Both tabs write through ``PUT /admin/training/modules/<id>/raw``.
 */
export function EditTrainingModuleDialog({
  isOpen,
  onOpenChange,
  moduleId,
}: EditTrainingModuleDialogProps) {
  const { t } = useTranslation()
  const endpoint = moduleId ? `/admin/training/modules/${encodeURIComponent(moduleId)}/raw` : null

  const {
    data: rawData,
    isLoading,
    refetch,
  } = useRawEntityQuery<StoredTrainingModule>(endpoint, isOpen && !!endpoint)

  const saveMutation = useSaveRawEntityMutation<StoredTrainingModule>({
    endpoint: endpoint ?? "",
    invalidateOnSuccess: [[...adminQueryKeys.trainingCatalog()]],
  })

  const stored: StoredTrainingModule = useMemo(() => rawData?.entity ?? {}, [rawData])
  const hash = rawData?.hash ?? ""

  const [enabled, setEnabled] = useState(true)
  const [displayName, setDisplayName] = useState("")
  const [resetSignal, setResetSignal] = useState(0)
  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    // Default to enabled when the override doc has no explicit `enabled`
    // key — otherwise opening the dialog and saving any other field would
    // silently flip a catalog-default-enabled module off.
    setEnabled(stored.enabled ?? true)
    setDisplayName(typeof stored.display_name === "string" ? stored.display_name : "")
    setResetSignal((n) => n + 1)
  }, [stored])

  useEffect(() => {
    if (!isOpen) setFormError(null)
  }, [isOpen])

  const handleFormSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!moduleId || !endpoint || !hash) return
    setFormError(null)

    const next: StoredTrainingModule = {
      ...stored,
      enabled,
      display_name: displayName.trim(),
    }

    try {
      await saveMutation.mutateAsync({
        entity: next as Record<string, unknown>,
        expectedHash: hash,
        reason: t("adminDashboard.trainingModuleEdit.reasonForm", {
          defaultValue: "Form-tab edit of training module {{id}}",
          id: moduleId,
        }),
        source: "form",
      })
      void refetch()
      toast.success(
        t("adminDashboard.trainingModuleEdit.toastSaved", {
          defaultValue: "Training module updated",
        })
      )
      onOpenChange(false)
    } catch (err) {
      setFormError(
        err instanceof Error
          ? err.message
          : t("adminDashboard.trainingModuleEdit.errorSaveFailed", {
              defaultValue: "Save failed",
            })
      )
    }
  }

  const handleRawSave = async (next: unknown, reason: string) => {
    if (!moduleId || !endpoint || !hash) return
    try {
      await saveMutation.mutateAsync({
        entity: (next || {}) as Record<string, unknown>,
        expectedHash: hash,
        reason,
        source: "raw",
      })
      void refetch()
      toast.success(
        t("adminDashboard.trainingModuleEdit.toastSavedRaw", {
          defaultValue: "Training module saved (raw)",
        })
      )
      onOpenChange(false)
    } catch (err) {
      toast.error(
        t("adminDashboard.trainingModuleEdit.toastRawSaveFailed", {
          defaultValue: "Raw save failed",
        }),
        { description: err instanceof Error ? err.message : undefined }
      )
      throw err
    }
  }

  if (!moduleId) return null

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[680px]">
        <DialogHeader>
          <DialogTitle>
            {t("adminDashboard.trainingModuleEdit.title", {
              defaultValue: "Edit training module",
            })}{" "}
            <code className="font-mono text-sm">{moduleId}</code>
            {stored.display_name ? (
              <span className="text-muted-foreground text-sm font-normal">
                {" "}
                ({stored.display_name})
              </span>
            ) : null}
          </DialogTitle>
          <DialogDescription>
            {t("adminDashboard.trainingModuleEdit.description", {
              defaultValue:
                "Edit display name and status. The Raw tab shows the full stored training module document.",
            })}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="form">
          <TabsList>
            <TabsTrigger value="form">
              {t("adminDashboard.trainingModuleEdit.tabForm", { defaultValue: "Form" })}
            </TabsTrigger>
            <TabsTrigger
              className="text-destructive data-[state=active]:text-destructive"
              value="raw"
            >
              {t("adminDashboard.trainingModuleEdit.tabRaw", { defaultValue: "Raw JSON" })}
            </TabsTrigger>
          </TabsList>

          <TabsContent className="mt-4 max-h-[60vh] overflow-y-auto" value="form">
            <form className="space-y-4" onSubmit={handleFormSubmit}>
              <div className="flex items-center justify-between gap-4 rounded-md border p-3">
                <div className="space-y-0.5">
                  <Label htmlFor="training-module-enabled">
                    {t("adminDashboard.trainingModuleEdit.fieldEnabled", {
                      defaultValue: "Enabled",
                    })}
                  </Label>
                  <p className="text-muted-foreground text-xs">
                    {t("adminDashboard.trainingModuleEdit.fieldEnabledHelp", {
                      defaultValue: "Disabled training modules are hidden from the product UI.",
                    })}
                  </p>
                </div>
                <Switch
                  checked={enabled}
                  id="training-module-enabled"
                  onCheckedChange={setEnabled}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="training-module-display-name">
                  {t("adminDashboard.trainingModuleEdit.fieldDisplayName", {
                    defaultValue: "Display name",
                  })}
                </Label>
                <Input
                  id="training-module-display-name"
                  value={displayName}
                  onChange={(event) => setDisplayName(event.target.value)}
                />
              </div>

              {formError ? <p className="text-destructive text-xs">{formError}</p> : null}

              <DialogFooter>
                <Button
                  disabled={saveMutation.isPending}
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  {t("adminDashboard.trainingModuleEdit.cancel", { defaultValue: "Cancel" })}
                </Button>
                <Button disabled={saveMutation.isPending || isLoading || !hash} type="submit">
                  {saveMutation.isPending ? (
                    <>
                      <Spinner />
                      {t("adminDashboard.trainingModuleEdit.saving", {
                        defaultValue: "Saving…",
                      })}
                    </>
                  ) : (
                    t("adminDashboard.trainingModuleEdit.save", { defaultValue: "Save" })
                  )}
                </Button>
              </DialogFooter>
            </form>
          </TabsContent>

          <TabsContent className="mt-4" value="raw">
            <RawJsonTab
              confirmToken={moduleId}
              isPending={saveMutation.isPending || !hash}
              resetSignal={resetSignal}
              value={stored}
              resourceLabel={t("adminDashboard.trainingModuleEdit.rawResourceLabel", {
                defaultValue: "training module {{id}}",
                id: moduleId,
              })}
              onSave={handleRawSave}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
